'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';

const aboutSections = [
  { id: 'about', label: 'About Us', href: '/about' },
  { id: 'history', label: 'History', href: '/about/history' },
  { id: 'mission', label: 'Mission', href: '/about/mission' },
  { id: 'vision', label: 'Vision', href: '/about/vision' },
  { id: 'membership', label: 'Membership', href: '/about/membership' },
  { id: 'quality', label: 'Quality Management', href: '/about/quality-management' },
  { id: 'ethics', label: 'Business Ethics', href: '/about/business-ethics' },
];

const timeline = [
  {
    year: '2001',
    title: 'The Beginning',
    text: 'WTO was born with offices soon following in Varna and Burgas. The initial main focus of the company was ocean freight service FCL.',
    icon: '',
    color: '#1B6B8A',
    highlight: 'Sofia, Varna & Burgas',
  },
  {
    year: '2007',
    title: 'Taking to the Skies',
    text: 'WTO expanded its portfolio to include airfreight service and inaugurated a new office in the airport of Sofia.',
    icon: '',
    color: '#1A5276',
    highlight: 'Sofia Airport Office',
  },
  {
    year: '2008',
    title: 'Own Consolidation Network',
    text: 'First own ocean consolidation services launched, connecting Asia and Eastern Europe. Within 2 years, the network grew exponentially — today WTO is one of the main players on the ocean consolidation market in Eastern and Central Europe.',
    icon: '',
    color: '#1B6B8A',
    highlight: 'Asia ↔ Eastern Europe',
  },
  {
    year: '2009',
    title: 'Going Regional',
    text: 'WTO opened own offices in Belgrade (Serbia), Zagreb (Croatia) and Koper (Slovenia), firming its position as a strong player on the Adriatic market.',
    icon: '',
    color: '#0F2340',
    highlight: 'Serbia · Croatia · Slovenia',
  },
  {
    year: '2013',
    title: 'Digital Transformation',
    text: 'Microsoft Dynamics Nav based ERP system implemented across all HQ offices and subsidiaries. ISO 9001:2008 certification achieved in 2014.',
    icon: '',
    color: '#1B6B8A',
    highlight: 'ISO 9001:2008 Certified',
  },
  {
    year: '2015',
    title: 'Forbes Recognition',
    text: 'First EDI projects with main customers and partners successfully completed. Awarded by Forbes Bulgaria for "Quality of Services" and "Customer Policy".',
    icon: '',
    color: '#C9A227',
    highlight: 'Forbes Award Winner',
  },
  {
    year: '2017',
    title: 'Smart Logistics',
    text: 'Automated cargo tracking notification service started. Purchase of land for construction of Logistics Center next to Sofia Airport. Second Forbes Bulgaria award.',
    icon: '',
    color: '#1A5276',
    highlight: 'Logistics Center Land Acquired',
  },
  {
    year: '2018',
    title: 'Building the Future',
    text: 'Certification of ISO 2008:2015 standard. Enhanced website functionality. Construction of the company\'s new Logistics Center nearby Sofia Airport initiated.',
    icon: '',
    color: '#1B6B8A',
    highlight: 'ISO 2015 Certified',
  },
  {
    year: '2020',
    title: 'Company of the Year',
    text: 'Honored with the "2020 Company of the Year" award in the Services sector in Bulgaria by Forbes. WTO opened its own company in China as an NVOCC operator.',
    icon: '',
    color: '#C9A227',
    highlight: 'Forbes Company of the Year',
  },
  {
    year: '2021',
    title: 'Vision 2030 in Motion',
    text: 'As a key step in the strategic plan "Vision 2030", WTO inaugurated own subsidiaries in Greece and N. Macedonia.',
    icon: '',
    color: '#1B6B8A',
    highlight: 'Greece · N. Macedonia',
  },
  {
    year: '2022',
    title: 'Expanding Horizons',
    text: 'WTO opened own offices in Taiwan, Kosovo, and Romania and integrated its ERP system with the leading logistics platform INTTRA. New office in Ruse, Bulgaria.',
    icon: '',
    color: '#1A5276',
    highlight: 'Taiwan · Kosovo · Romania',
  },
  {
    year: '2023',
    title: 'Fastest Growing',
    text: 'WTO China expanded with new offices in Ningbo and Chongqing. New office in Albania. The Capital newspaper recognized WTO as the fastest-growing company in Transport & Logistics.',
    icon: '',
    color: '#1B6B8A',
    highlight: 'Fastest Growing — Capital',
  },
  {
    year: '2024',
    title: 'Forbes Again',
    text: 'New subsidiary in Bosnia and Herzegovina and a new office in Shenzhen, China. WTO received the prestigious Forbes Company of the Year 2024 award in the Services sector.',
    icon: '',
    color: '#C9A227',
    highlight: 'Forbes Company of the Year 2024',
  },
  {
    year: '2025',
    title: 'Global Intelligence',
    text: 'New offices in Germany, the United States, and Vietnam. Implementation of CargoWise — a next-generation, world-leading logistics platform unifying all processes into one intelligent system.',
    icon: '',
    color: '#1B6B8A',
    highlight: 'Germany · USA · Vietnam',
  },
  {
    year: '2026',
    title: 'Forbes Company of the Year 2026',
    text: 'Development of WTO Logistics Center in Koper, Slovenia — a strategic hub on the Adriatic corridor. WTO honored with the Forbes Company of the Year 2026 award in the Services sector for WTO HQ.',
    icon: '',
    color: '#C9A227',
    highlight: 'Forbes Company of the Year 2026',
  },
];

const stats = [
  { value: 25, suffix: '+', label: 'Years of Excellence' },
  { value: 16, suffix: '+', label: 'Countries' },
  { value: 15, suffix: '', label: 'Milestones' },
  { value: 5, suffix: 'x', label: 'Forbes Awards' },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function AnimatedCounter({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView(0.5);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);
  return (
    <div ref={ref} className="text-4xl font-bold" style={{ color: '#7EC8DC' }}>
      {count}{suffix}
    </div>
  );
}

function MilestoneCard({ item, index }: { item: typeof timeline[0]; index: number }) {
  const { ref, inView } = useInView(0.1);
  const [hovered, setHovered] = useState(false);
  const isLeft = index % 2 === 0;

  return (
    <div
      ref={ref}
      className="relative grid w-full"
      style={{
        gridTemplateColumns: '1fr 80px 1fr',
        opacity: inView ? 1 : 0,
        transform: inView
          ? 'translateY(0) scale(1)'
          : `translateY(40px) scale(0.97)`,
        transition: `opacity 0.6s cubic-bezier(0.22,1,0.36,1) ${index * 0.07}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${index * 0.07}s`,
      }}
    >
      {/* Left side */}
      <div className="flex justify-end pr-6 items-center">
        {isLeft ? (
          <div
            className="inline-block rounded-2xl p-5 cursor-default text-right"
            style={{
              background: hovered ? '#FFFFFF' : 'rgba(255,255,255,0.97)',
              border: `1px solid ${hovered ? item.color : 'rgba(15,35,64,0.1)'}`,
              boxShadow: hovered
                ? `0 12px 40px rgba(15,35,64,0.14), 0 0 0 2px ${item.color}22`
                : '0 2px 12px rgba(15,35,64,0.07)',
              transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
              transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <div className="flex items-center justify-end gap-2 mb-2">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: item.color }}>{item.year}</span>
            </div>
            <h3 className="text-base font-bold mb-1 font-body" style={{ color: '#0F2340' }}>{item.title}</h3>
            <p className="text-sm leading-relaxed font-body" style={{ color: '#3A5070' }}>{item.text}</p>
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: `${item.color}15`, color: item.color }}>
              {item.highlight}
            </div>
          </div>
        ) : null}
      </div>

      {/* Center spine — always truly centered */}
      <div className="flex flex-col items-center justify-center relative">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center z-10 relative"
          style={{
            background: hovered ? item.color : '#FFFFFF',
            border: `3px solid ${item.color}`,
            boxShadow: hovered ? `0 0 0 6px ${item.color}22, 0 4px 16px ${item.color}44` : `0 0 0 4px ${item.color}18`,
            transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
            transform: hovered ? 'scale(1.15)' : 'scale(1)',
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
        <div
          className="absolute text-xs font-bold font-body px-2 py-0.5 rounded-full"
          style={{
            background: item.color,
            color: '#FFFFFF',
            top: '2.8rem',
            whiteSpace: 'nowrap',
            fontSize: '10px',
            letterSpacing: '0.05em',
          }}
        >
          {item.year}
        </div>
      </div>

      {/* Right side */}
      <div className="flex justify-start pl-6 items-center">
        {!isLeft ? (
          <div
            className="inline-block rounded-2xl p-5 cursor-default text-left"
            style={{
              background: hovered ? '#FFFFFF' : 'rgba(255,255,255,0.97)',
              border: `1px solid ${hovered ? item.color : 'rgba(15,35,64,0.1)'}`,
              boxShadow: hovered
                ? `0 12px 40px rgba(15,35,64,0.14), 0 0 0 2px ${item.color}22`
                : '0 2px 12px rgba(15,35,64,0.07)',
              transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
              transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: item.color }}>{item.year}</span>
            </div>
            <h3 className="text-base font-bold mb-1 font-body" style={{ color: '#0F2340' }}>{item.title}</h3>
            <p className="text-sm leading-relaxed font-body" style={{ color: '#3A5070' }}>{item.text}</p>
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: `${item.color}15`, color: item.color }}>
              {item.highlight}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MobileCard({ item, index }: { item: typeof timeline[0]; index: number }) {
  const { ref, inView } = useInView(0.1);
  const [hovered, setHovered] = useState(false);
  return (
    <div
      ref={ref}
      className="flex gap-4 items-start"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateX(0)' : 'translateX(-24px)',
        transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1) ${index * 0.06}s, transform 0.55s cubic-bezier(0.22,1,0.36,1) ${index * 0.06}s`,
      }}
    >
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-base"
          style={{
            background: hovered ? item.color : '#FFFFFF',
            border: `2.5px solid ${item.color}`,
            boxShadow: `0 0 0 4px ${item.color}18`,
            transition: 'all 0.3s',
          }}
        />
        <div className="w-px flex-1 mt-2" style={{ background: `${item.color}40`, minHeight: '24px' }} />
      </div>
      <div
        className="flex-1 rounded-2xl p-4 mb-6"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${hovered ? item.color : 'rgba(15,35,64,0.1)'}`,
          boxShadow: hovered ? `0 8px 24px rgba(15,35,64,0.12)` : '0 2px 8px rgba(15,35,64,0.06)',
          transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
          transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: item.color, color: '#FFF', fontSize: '10px' }}>{item.year}</span>
        </div>
        <h3 className="text-sm font-bold mb-1 font-body" style={{ color: '#0F2340' }}>{item.title}</h3>
        <p className="text-xs leading-relaxed font-body" style={{ color: '#3A5070' }}>{item.text}</p>
        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: `${item.color}15`, color: item.color, fontSize: '10px' }}>
          {item.highlight}
        </div>
      </div>
    </div>
  );
}

export default function HistoryPage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [activeYear, setActiveYear] = useState('2001');

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen overflow-x-hidden" style={{ background: '#F4F6F9', color: '#0F2340' }}>

        {/* ── Hero with parallax ── */}
        <div
          ref={heroRef}
          className="relative pt-32 pb-20 px-6 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0F2340 0%, #1A3558 60%, #1B6B8A 100%)' }}
        >
          {/* Animated background orbs */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              className="absolute rounded-full"
              style={{
                width: '600px', height: '600px',
                background: 'radial-gradient(circle, rgba(27,107,138,0.25) 0%, transparent 70%)',
                top: '-200px', right: '-100px',
                transform: `translateY(${scrollY * 0.15}px)`,
                transition: 'transform 0.1s linear',
              }}
            />
            <div
              className="absolute rounded-full"
              style={{
                width: '400px', height: '400px',
                background: 'radial-gradient(circle, rgba(126,200,220,0.12) 0%, transparent 70%)',
                bottom: '-100px', left: '10%',
                transform: `translateY(${scrollY * -0.1}px)`,
                transition: 'transform 0.1s linear',
              }}
            />
            {/* Floating dots */}
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: `${4 + (i % 4) * 3}px`,
                  height: `${4 + (i % 4) * 3}px`,
                  background: `rgba(126,200,220,${0.15 + (i % 3) * 0.1})`,
                  left: `${8 + i * 7.5}%`,
                  top: `${15 + (i % 5) * 15}%`,
                  transform: `translateY(${scrollY * (0.05 + i * 0.01)}px)`,
                  transition: 'transform 0.1s linear',
                  animation: `float${i % 3} ${3 + i % 4}s ease-in-out infinite alternate`,
                }}
              />
            ))}
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            <Link
              href="/about"
              className="inline-flex items-center gap-2 text-sm font-body mb-6 transition-all duration-200 group"
              style={{ color: '#7EB8F7' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#7EB8F7'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-x-1 transition-transform duration-200"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
              The Company
            </Link>
            <p className="text-xs uppercase tracking-widest font-body mb-3" style={{ color: '#7EB8F7' }}>World Transport Overseas</p>
            <h1
              className="text-5xl lg:text-6xl font-bold font-body mb-5"
              style={{
                color: '#FFFFFF',
                transform: `translateY(${scrollY * -0.08}px)`,
                transition: 'transform 0.1s linear',
              }}
            >
              Our History
            </h1>
            <p className="text-lg font-body max-w-2xl mb-10" style={{ color: 'rgba(200,216,240,0.8)' }}>
              From a single office in Sofia to a global logistics network spanning 16 countries — 24 years of relentless growth.
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl p-4 text-center"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}
                >
                  <AnimatedCounter target={s.value} suffix={s.suffix} />
                  <div className="text-xs mt-1 font-body" style={{ color: 'rgba(200,216,240,0.7)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Sub-nav ── */}
        <div className="sticky top-20 z-30 px-6 py-3" style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid rgba(15,35,64,0.1)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-5xl mx-auto flex flex-wrap gap-1">
            {aboutSections?.map((s) => (
              <Link
                key={s?.id}
                href={s?.href}
                className="px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200"
                style={s?.id === 'history'
                  ? { background: '#1B6B8A', color: '#FFFFFF', border: '1px solid rgba(27,107,138,0.5)' }
                  : { background: 'transparent', color: '#3A5070', border: '1px solid rgba(15,35,64,0.15)' }}
                onMouseEnter={(e) => { if (s?.id !== 'history') { e.currentTarget.style.background = 'rgba(27,107,138,0.1)'; e.currentTarget.style.color = '#0F2340'; } }}
                onMouseLeave={(e) => { if (s?.id !== 'history') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#3A5070'; } }}
              >
                {s?.label}
              </Link>
            ))}
          </div>
        </div>

        {/* ── Intro ── */}
        <div className="max-w-4xl mx-auto px-6 pt-14 pb-4">
          <p className="text-base font-body leading-relaxed" style={{ color: '#3A5070' }}>
            Welcome to World Transport Overseas — a leading freight forwarding company with offices across 16 countries in Europe, Asia, and the Americas. We offer reliable sea freight, fast and secure air freight, and comprehensive road transport services. Our extensive network of own subsidiaries and representatives ensures global coverage with local expertise.
          </p>
        </div>

        {/* ── Desktop alternating timeline ── */}
        <div className="hidden md:block max-w-5xl mx-auto px-6 py-12 relative">
          {/* Center spine */}
          <div
            className="absolute top-12 bottom-12 w-0.5 left-1/2 -translate-x-1/2"
            style={{ background: 'linear-gradient(to bottom, transparent, rgba(27,107,138,0.3) 5%, rgba(27,107,138,0.3) 95%, transparent)' }}
          />
          <div className="space-y-10">
            {timeline.map((item, i) => (
              <MilestoneCard key={item.year} item={item} index={i} />
            ))}
          </div>
        </div>

        {/* ── Mobile timeline ── */}
        <div className="md:hidden max-w-xl mx-auto px-5 py-10">
          {timeline.map((item, i) => (
            <MobileCard key={item.year} item={item} index={i} />
          ))}
        </div>

        {/* ── Bottom CTA banner ── */}
        <BottomBanner />

        <style jsx global>{`
          @keyframes float0 { from { transform: translateY(0px); } to { transform: translateY(-10px); } }
          @keyframes float1 { from { transform: translateY(0px); } to { transform: translateY(-16px); } }
          @keyframes float2 { from { transform: translateY(0px); } to { transform: translateY(-8px); } }
        `}</style>
      </main>
    </>
  );
}

function BottomBanner() {
  const { ref, inView } = useInView(0.2);
  return (
    <div
      ref={ref}
      className="mx-6 mb-16 rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0F2340 0%, #1B6B8A 100%)',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
        maxWidth: '1024px',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <div className="px-10 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-widest mb-2 font-body" style={{ color: '#7EC8DC' }}>The journey continues</p>
          <h2 className="text-2xl md:text-3xl font-bold font-body" style={{ color: '#FFFFFF' }}>Be part of our next chapter</h2>
          <p className="mt-2 text-sm font-body" style={{ color: 'rgba(200,216,240,0.75)' }}>25 years of growth — and we're just getting started.</p>
        </div>
        <Link
          href="/contacts"
          className="flex-shrink-0 px-8 py-3 rounded-full font-bold font-body text-sm transition-all duration-200"
          style={{ background: '#3D9B7A', color: '#FFFFFF' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#2e7d5e'; e.currentTarget.style.transform = 'scale(1.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#3D9B7A'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
          Get in Touch
        </Link>
      </div>
    </div>
  );
}
